import { useCallback, useEffect, useRef, useState } from "react";

interface UsePollingOptions<T> {
  /** 轮询间隔 ms，默认 4000 */
  intervalMs?: number;
  /** 是否启用，默认 true */
  enabled?: boolean;
  /** 加载函数 */
  fetcher: () => Promise<T>;
}

/**
 * 轻量轮询：挂载拉一次，之后按间隔刷新；卸载自动停止。
 */
export function usePolling<T>({
  fetcher,
  intervalMs = 4000,
  enabled = true,
}: UsePollingOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const next = await fetcherRef.current();
      setData(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const next = await fetcherRef.current();
        if (!cancelled) {
          setData(next);
          setError(null);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "加载失败");
          setLoading(false);
        }
      }
      if (!cancelled) {
        timer = setTimeout(tick, intervalMs);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, intervalMs]);

  return { data, error, loading, refresh };
}
